import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import { Login, Signup } from "@/pages/Auth";
import { CookieBanner } from "@/components/CookieBanner";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";

const AppLayout = lazy(() => import("@/pages/app/AppLayout"));
const BuildHome = lazy(() => import("@/pages/app/BuildHome"));
const ProjectPage = lazy(() => import("@/pages/app/ProjectPage"));
const Projects = lazy(() => import("@/pages/app/Projects"));
const Settings = lazy(() => import("@/pages/app/Settings"));
const ComingSoon = lazy(() => import("@/pages/app/ComingSoon"));
const Apps = lazy(() => import("@/pages/app/Apps"));
const DataExplorer = lazy(() => import("@/pages/app/DataExplorer"));
const Agents = lazy(() => import("@/pages/app/Agents"));
const Integrations = lazy(() => import("@/pages/app/Integrations"));
const Automations = lazy(() => import("@/pages/app/Automations"));
const Growth = lazy(() => import("@/pages/app/Growth"));
const Billing = lazy(() => import("@/pages/app/Billing"));
const Legal = lazy(() => import("@/pages/Legal"));

const Loader = () => <div className="grid min-h-screen place-items-center"><div className="h-10 w-10 animate-spin rounded-full border-2 border-white/10 border-t-[#7a5cff]" /></div>;

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const SOON = ["deployments", "monitor"];

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Suspense fallback={<Loader />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/legal" element={<Legal />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/legal/:slug" element={<Legal />} />
            <Route path="/studio" element={<Navigate to="/app" replace />} />
            <Route path="/app" element={<Protected><AppLayout /></Protected>}>
              <Route index element={<BuildHome />} />
              <Route path="projects" element={<Projects />} />
              <Route path="projects/:id" element={<ProjectPage />} />
              <Route path="settings" element={<Settings />} />
              <Route path="apps" element={<Apps />} />
              <Route path="data" element={<DataExplorer />} />
              <Route path="agents" element={<Agents />} />
              <Route path="integrations" element={<Integrations />} />
              <Route path="automations" element={<Automations />} />
              <Route path="growth" element={<Growth />} />
              <Route path="billing" element={<Billing />} />
              {SOON.map((s) => <Route key={s} path={s} element={<ComingSoon section={s} />} />)}
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        <CookieBanner />
      </BrowserRouter>
      <Toaster theme="dark" richColors position="top-right" />
    </AuthProvider>
  );
}

export default App;
